import { format } from 'date-fns';
import { studioDayKey } from '@atink/domain';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useUIStore } from '@/state/uiStore';
import { eventBus } from '@/events/eventBus';
import { isStoreFallbackEnabled } from '@/api/apiMode';
import { topupExtendPayByIntent } from '@/api/intents';
import { withServerAuthoredWrites } from '@/state/persistenceBridge';
import type { TopupInstalment, TopupPlan } from '@/domain/types';

// ---------------------------------------------------------------------------
// Ops extend pay-by date (vault System Diagram 09, extend branch). Extending
// pushes the current top-up / deposit pay-by deadline out to a new date and
// re-points the reminder to it: the instalment is reset to `scheduled` so the
// existing top-up scheduler (reconcileTopupSchedule) fires the reminder on the
// NEW date and the cadence continues normally from there. No new reminder
// machinery - it re-uses the instalment reminder schedule already in place.
// An approved extension also starts the missed top-up chase again (Anas 1 Oct,
// tv-toef): served, the server does it (POST /api/intents/topup-extend-payby),
// because B8's chase gate rightly refuses a client write that resets the count.
// "Now" is the sim clock, never Date.now().
// ---------------------------------------------------------------------------

function nowIso(): string {
  return useSimulationStore.getState().clockIso;
}

export interface OpsExtendInput {
  /** The top-up plan whose current pay-by date is being pushed out. */
  planId: string;
  /** The new pay-by / due date (ISO). Must be later than the current due date. */
  newDueIso: string;
  /** Persona / person extending (Ops). */
  by: string;
  at?: string;
}

export interface OpsExtendResult {
  ok: boolean;
  planId?: string;
  previousDueIso?: string;
  newDueIso?: string;
  error?: 'no-plan' | 'not-active' | 'no-instalment' | 'not-later';
  /** Served: settles true once the server has extended it (and reset the chase), false if it refused. */
  completion?: Promise<boolean>;
}

/**
 * The current instalment being chased: the earliest one still unsettled (not
 * `paid`/`cancelled`). For an inside-7d extension this is the `missed` one; for a
 * pay-by extension it is the `scheduled`/`reminded` one that has come due.
 */
function currentInstalmentIndex(instalments: TopupInstalment[]): number {
  return instalments.findIndex((i) => i.status !== 'paid' && i.status !== 'cancelled');
}

/**
 * Ops extends a top-up / deposit pay-by date. Pushes the current instalment's due
 * date out to `newDueIso` and re-points the reminder: the instalment is reset to
 * `scheduled` and its `remindedAt` cleared, so `reconcileTopupSchedule` fires the
 * reminder on the new date and continues the cadence from there. Only pushes the
 * date OUT - an earlier or equal date is refused (`not-later`).
 */
export function opsExtendPayBy(input: OpsExtendInput): OpsExtendResult {
  const brain = useBrainStore.getState();
  const plan = brain.topupPlans[input.planId];
  if (!plan) return { ok: false, error: 'no-plan' };
  if (plan.status !== 'active') return { ok: false, error: 'not-active' };

  const idx = currentInstalmentIndex(plan.instalments);
  if (idx === -1) return { ok: false, error: 'no-instalment' };

  const current = plan.instalments[idx]!;
  const previousDueIso = current.dueIso;
  if (new Date(input.newDueIso).getTime() <= new Date(previousDueIso).getTime()) {
    return { ok: false, error: 'not-later' };
  }

  const at = input.at ?? nowIso();
  // Re-point the reminder: move the due date out and re-arm the instalment so the
  // scheduler reminds on the NEW date (remindedAt cleared, status back to scheduled),
  // and start the chase again (count back to 0).
  const instalments = plan.instalments.map((inst, i) =>
    i === idx
      ? { ...inst, dueIso: input.newDueIso, status: 'scheduled' as const, remindedAt: undefined }
      : inst,
  );
  const extended: TopupPlan = { ...plan, instalments, chaseAttempts: 0, lastChaseAt: undefined };

  const recordSuccess = () => {
    useBrainStore.getState().appendActivity({
      // Reuse the closest existing ActivityType (top-up plan lifecycle, as
      // createTopupPlan does) so the exhaustive ActivityFeedItem icon map stays
      // untouched - no new union member for one flow.
      id: `act-ops-extend-${plan.id}-${at}`,
      type: 'deposit-link-sent',
      customerId: plan.customerId,
      studioId: plan.studioId,
      body: `Ops extended the pay-by date to ${format(new Date(`${studioDayKey(input.newDueIso)}T00:00:00`), 'EEE d MMM')} by ${input.by} - reminder re-pointed to the new date, chase count reset.`,
      timestamp: at,
    });
    eventBus.publish('ops:pay-by-extended', {
      planId: plan.id,
      depositId: plan.depositId,
      customerId: plan.customerId,
      previousDueIso,
      newDueIso: input.newDueIso,
      at,
    });
  };

  const ok: OpsExtendResult = { ok: true, planId: plan.id, previousDueIso, newDueIso: input.newDueIso };
  if (isStoreFallbackEnabled()) {
    brain.upsertTopupPlan(extended);
    recordSuccess();
    return ok;
  }

  // Served: the server extends it and resets the chase in one transaction; the
  // store mirrors its schedule only once it says yes (no optimistic write to revert).
  const completion = topupExtendPayByIntent(plan.id, input.newDueIso).then((res) => {
    if (!res.ok) {
      useUIStore.getState().pushNotification({
        intensity: 'critical',
        title: "Pay-by wasn't extended",
        body: res.message ?? 'The server refused it. Try again.',
        ttlMs: 8000,
      });
      return false;
    }
    withServerAuthoredWrites([['topupPlans', plan.id]], () => {
      const current = useBrainStore.getState().topupPlans[plan.id] ?? plan;
      useBrainStore.getState().upsertTopupPlan({
        ...current,
        chaseAttempts: 0,
        lastChaseAt: undefined,
        instalments:
          res.instalments.length > 0
            ? res.instalments.map((i) => ({
                dueIso: i.dueIso,
                amountAud: i.amountAud,
                status: i.status as TopupInstalment['status'],
                ...(i.paidAt ? { paidAt: i.paidAt } : {}),
              }))
            : instalments,
      });
    });
    recordSuccess();
    return true;
  });
  return { ...ok, completion };
}
